/**
 * AI Assistant Drawer
 *
 * Main chatbot interface with message history, input, suggested actions, and insights
 */

import { useState, useRef, useEffect } from 'react';
import {
  Drawer,
  Input,
  Button,
  Space,
  Spin,
  Alert,
  Typography,
  Divider,
  Empty,
  Tooltip,
  Badge,
} from 'antd';
import {
  SendOutlined,
  RobotOutlined,
  DeleteOutlined,
  PlusOutlined,
  BulbOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import useAIStore from '../../store/aiStore';
import { processQuery } from '../../api/ai';
import ChatMessageBubble from './ChatMessageBubble';
import SuggestedActionsPanel from './SuggestedActionsPanel';
import SmartInsightsPanel from './SmartInsightsPanel';
import useThemeStore from '../../store/themeStore';

const { TextArea } = Input;
const { Text } = Typography;

export default function AIAssistantDrawer() {
  const {
    isOpen,
    close,
    isLoading,
    setLoading,
    error,
    setError,
    getCurrentConversation,
    addUserMessage,
    addAssistantMessage,
    createConversation,
    setCurrentConversation,
    conversations,
    currentConversationId,
    deleteConversation,
    context,
    suggestedActions,
    insights,
  } = useAIStore();

  const { resolvedTheme } = useThemeStore();
  const isDark = resolvedTheme === 'dark';

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<any>(null);

  const currentConversation = getCurrentConversation();

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [currentConversation?.messages]);

  // Focus input when drawer opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }
  }, [isOpen]);

  // Handle send message
  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;

    const message = inputValue.trim();
    setInputValue('');
    setError(null);

    try {
      setIsSending(true);
      setLoading(true);

      // Add user message
      addUserMessage(message);

      // Build message history for context
      const messages = currentConversation?.messages.map((m) => ({
        role: m.role as 'user' | 'assistant' | 'system',
        content: m.content,
      })) || [];

      messages.push({
        role: 'user',
        content: message,
      });

      // Call AI service with real API
      const queryType = 'general'; // Determine based on message content or context
      const response = await processQuery(message, queryType, context || undefined);

      // Add assistant response
      addAssistantMessage(response.response_text, [], []);
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to get response from AI assistant. Please try again.');
    } finally {
      setIsSending(false);
      setLoading(false);
    }
  };

  // Handle key press (Enter to send, Shift+Enter for new line)
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Handle new conversation
  const handleNewConversation = () => {
    const id = createConversation();
    setCurrentConversation(id);
    setInputValue('');
    setError(null);
  };

  // Handle delete conversation
  const handleDeleteConversation = () => {
    if (currentConversationId) {
      deleteConversation(currentConversationId);
    }
  };

  // Quick prompts
  const quickPrompts = [
    'Show me an overview of my business',
    'What invoices are overdue?',
    'Create a new invoice',
    'Show me this month\'s revenue',
    'What items need reordering?',
  ];

  const handleQuickPrompt = (prompt: string) => {
    setInputValue(prompt);
    inputRef.current?.focus();
  };

  return (
    <Drawer
      title={
        <Space>
          <RobotOutlined style={{ fontSize: 18, color: '#1890ff' }} />
          <span>AI Assistant</span>
          {insights.filter((i) => !i.dismissed).length > 0 && (
            <Badge count={insights.filter((i) => !i.dismissed).length} />
          )}
        </Space>
      }
      placement="right"
      open={isOpen}
      onClose={close}
      width={480}
      extra={
        <Space>
          <Tooltip title="New Conversation">
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={handleNewConversation}
            />
          </Tooltip>
          {currentConversationId && conversations.length > 1 && (
            <Tooltip title="Delete Conversation">
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={handleDeleteConversation}
              />
            </Tooltip>
          )}
        </Space>
      }
      styles={{
        body: { padding: 0, display: 'flex', flexDirection: 'column', height: 'calc(100vh - 55px)' },
      }}
    >
      {/* Messages Area */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
        {!currentConversation || currentConversation.messages.length === 0 ? (
          <div style={{ padding: '40px 0' }}>
            <Empty
              image={<RobotOutlined style={{ fontSize: 64, color: '#1890ff' }} />}
              description={
                <div>
                  <Text strong style={{ fontSize: 16 }}>
                    Welcome to AI Assistant
                  </Text>
                  <br />
                  <Text type="secondary">
                    Ask me anything about your business, invoices, payments, inventory, and more.
                  </Text>
                </div>
              }
            >
              <div style={{ marginTop: 24 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Try these questions:
                </Text>
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  {quickPrompts.map((prompt, index) => (
                    <Button
                      key={index}
                      block
                      size="small"
                      onClick={() => handleQuickPrompt(prompt)}
                      style={{ textAlign: 'left', height: 'auto', padding: '8px 12px' }}
                    >
                      {prompt}
                    </Button>
                  ))}
                </Space>
              </div>
            </Empty>
          </div>
        ) : (
          <>
            {currentConversation.messages.map((message) => (
              <ChatMessageBubble key={message.id} message={message} />
            ))}
            {isLoading && (
              <div style={{ padding: '16px 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                <Spin spinning />
                <div style={{ color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' }}>Thinking...</div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Error Alert */}
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ margin: '0 16px 8px' }}
        />
      )}

      {/* Suggested Actions Panel */}
      {suggestedActions.length > 0 && (
        <div style={{ padding: '0 16px 8px' }}>
          <SuggestedActionsPanel actions={suggestedActions} />
        </div>
      )}

      {/* Smart Insights Panel */}
      {insights.filter((i) => !i.dismissed).length > 0 && (
        <div style={{ padding: '0 16px 8px' }}>
          <SmartInsightsPanel insights={insights.filter((i) => !i.dismissed)} />
        </div>
      )}

      {/* Input Area */}
      <div
        style={{
          padding: '16px',
          borderTop: isDark ? '1px solid #303030' : '1px solid #f0f0f0',
          background: isDark ? '#141414' : '#fafafa',
        }}
      >
        <Space.Compact style={{ width: '100%' }}>
          <TextArea
            ref={inputRef}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything... (Shift+Enter for new line)"
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={isSending}
            style={{ resize: 'none' }}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={handleSend}
            loading={isSending}
            disabled={!inputValue.trim() || isSending}
            style={{ height: 'auto' }}
          >
            Send
          </Button>
        </Space.Compact>

        <div style={{ marginTop: 8 }}>
          <Space split={<Divider type="vertical" />} size="small">
            <Tooltip title="Suggested Actions">
              <Button
                type="text"
                size="small"
                icon={<ThunderboltOutlined />}
                style={{ fontSize: 12, padding: '0 4px' }}
              >
                {suggestedActions.length} Actions
              </Button>
            </Tooltip>
            <Tooltip title="Smart Insights">
              <Button
                type="text"
                size="small"
                icon={<BulbOutlined />}
                style={{ fontSize: 12, padding: '0 4px' }}
              >
                {insights.filter((i) => !i.dismissed).length} Insights
              </Button>
            </Tooltip>
          </Space>
        </div>
      </div>
    </Drawer>
  );
}
